// client/src/contexts/CommandPaletteContext.tsx
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { isTypesenseEnabled } from '../config/typesense';

interface CommandPaletteContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

/**
 * CommandPaletteProvider — manages global search state at the app root.
 *
 * Placing this above all module error boundaries ensures:
 * 1. CTRL+K works from ANY page regardless of which module is active.
 * 2. A crash inside CommandPalette does NOT take down the current module.
 *
 * Navigation reads `open()` from this context to wire its search button.
 */
export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  // Global CTRL+K / CMD+K shortcut — registered once at the app root.
  useEffect(() => {
    if (!isTypesenseEnabled()) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggle();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggle]);

  return (
    <CommandPaletteContext.Provider value={{ isOpen, open, close, toggle }}>
      {children}
    </CommandPaletteContext.Provider>
  );
}

export function useCommandPalette(): CommandPaletteContextValue {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    throw new Error('useCommandPalette must be used within CommandPaletteProvider');
  }
  return ctx;
}
